import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ParticleField } from "@/components/effects/ParticleField";
import { useLanguage } from "@/i18n/LanguageContext";

const empty = { name: "", email: "", company: "", message: "", industry: "", budget: "" };

const PillGroup = ({ label, options, value, onSelect }) => (
  <div className="flex flex-col gap-2.5">
    <Label>{label}</Label>
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            type="button"
            key={opt}
            onClick={() => onSelect(active ? "" : opt)}
            className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              active
                ? "border-primary bg-primary text-primary-foreground shadow-glow-soft"
                : "border-border/60 bg-secondary/40 text-muted-foreground hover:border-primary/50 hover:text-foreground"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k) => (val) => setForm((f) => ({ ...f, [k]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch("/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(t.contact.success);
        setForm(empty);
      } else {
        toast.error("Bir hata oluştu, lütfen tekrar deneyin.");
      }
    } catch {
      toast.error("Sunucuya ulaşılamadı, lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Mail, label: t.contact.info.emailLabel, value: t.contact.info.email },
    { icon: Phone, label: t.contact.info.phoneLabel, value: t.contact.info.phone },
    { icon: MapPin, label: t.contact.info.addressLabel, value: t.contact.info.address },
    { icon: Phone, label: t.contact.info.phoneTrLabel, value: t.contact.info.phoneTr },
    { icon: MapPin, label: t.contact.info.addressTrLabel, value: t.contact.info.addressTr },
  ];

  return (
    <section id="contact" className="relative overflow-hidden py-24 sm:py-32">
      <ParticleField count={40} />
      <div className="absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/20 blur-[150px] animate-glow-pulse" />
      <div className="relative mx-auto max-w-6xl px-6">
        <div className="glass-strong overflow-hidden rounded-[2rem] shadow-elegant">
          <div className="grid lg:grid-cols-2">
            {/* Left info */}
            <div className="relative flex flex-col justify-between gap-10 border-b border-border/50 p-8 sm:p-12 lg:border-b-0 lg:border-r">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-glow-pulse" />
                  {t.contact.tag}
                </span>
                <h2 className="mt-5 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
                  {t.contact.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                  {t.contact.subtitle}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground/80">
                  {t.contact.note}
                </p>
              </div>

              <div className="space-y-4">
                {info.map((it) => (
                  <div key={it.label} className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
                      <it.icon className="h-5 w-5" />
                    </span>
                    <div className="leading-tight">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">{it.label}</p>
                      <p className="font-medium text-foreground">{it.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right form */}
            <div className="p-8 sm:p-12">
              <form onSubmit={submit} className="flex flex-col gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="name">{t.contact.fields.name}</Label>
                    <Input id="name" required value={form.name} onChange={set("name")} className="h-12 bg-secondary/40" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="email">{t.contact.fields.email}</Label>
                    <Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-12 bg-secondary/40" />
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="company">{t.contact.fields.company}</Label>
                  <Input id="company" value={form.company} onChange={set("company")} className="h-12 bg-secondary/40" />
                </div>

                <PillGroup
                  label={t.contact.industryLabel}
                  options={t.contact.industries}
                  value={form.industry}
                  onSelect={pick("industry")}
                />
                <PillGroup
                  label={t.contact.budgetLabel}
                  options={t.contact.budgets}
                  value={form.budget}
                  onSelect={pick("budget")}
                />

                <div className="flex flex-col gap-2">
                  <Label htmlFor="message">{t.contact.fields.message}</Label>
                  <Textarea id="message" required rows={4} value={form.message} onChange={set("message")} className="resize-none bg-secondary/40" />
                </div>
                <Button type="submit" variant="hero" size="xl" disabled={loading} className="mt-2 w-full">
                  {loading ? t.contact.sending : t.contact.button}
                  {!loading && <ArrowUpRight className="h-5 w-5" />}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
